// ABOUTME: Reusable hierarchy list component for left column in two-column layouts
// ABOUTME: Displays hierarchies with selection state, count chips, and loading/error states

import { alpha, Box, Chip, CircularProgress, Stack, Typography } from '@ds/coolshop';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

type IHierarchy = {
    hierarchyId: string;
    hierarchyCode: string;
    objectSchemaCode: string;
    totalCount: number;
};

type IHierarchyListProps = {
    hierarchies: IHierarchy[];
    selectedHierarchy: IHierarchy | null;
    onHierarchySelect: (hierarchy: IHierarchy) => void;
    loading?: boolean;
    error?: string;
    emptyMessage?: string;
    errorMessageId?: string;
    noHierarchiesMessageId?: string;
};

export function HierarchyList(props: IHierarchyListProps) {
    const {
        hierarchies,
        selectedHierarchy,
        onHierarchySelect,
        loading = false,
        error,
        emptyMessage,
        errorMessageId = 'hierarchy-list.error',
        noHierarchiesMessageId = 'hierarchy-list.no-hierarchies',
    } = props;

    const { formatMessage } = useIntl();

    const getHierarchyPrefix = useCallback(
        (hierarchy: IHierarchy) => hierarchy.hierarchyCode.split(':')[0],
        [],
    );

    if (loading) {
        return (
            <Box textAlign="center" width={1} height={1} display="flex" justifyContent="center">
                <CircularProgress value={12} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={2}>
                <Typography variant="body2" color="error">
                    {emptyMessage ||
                        formatMessage({
                            id: errorMessageId,
                            defaultMessage: 'Error loading hierarchies',
                        })}
                </Typography>
            </Box>
        );
    }

    if (hierarchies.length === 0) {
        return (
            <Box p={2}>
                <Typography variant="body2">
                    {formatMessage({
                        id: noHierarchiesMessageId,
                        defaultMessage: 'No hierarchies found',
                    })}
                </Typography>
            </Box>
        );
    }

    return (
        <Box overflow="auto" height="100%" py={1.5}>
            {hierarchies.map(hierarchy => {
                const isSelected = selectedHierarchy?.hierarchyCode === hierarchy.hierarchyCode;
                return (
                    <Stack
                        key={`${hierarchy.hierarchyId}-${hierarchy.hierarchyCode}`}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        p={1.5}
                        onClick={() => {
                            onHierarchySelect(hierarchy);
                        }}
                        cursor="pointer"
                        borderRadius={2}
                        bgcolor={t =>
                            isSelected ? alpha(t.palette.action.active, 0.1) : 'inerith'
                        }
                    >
                        <Box flexGrow={1}>
                            <Typography variant="body2">{getHierarchyPrefix(hierarchy)}</Typography>
                        </Box>
                        <Chip
                            label={hierarchy.totalCount.toString()}
                            size="small"
                            color="primary"
                        />
                    </Stack>
                );
            })}
        </Box>
    );
}
